import { PersonaCardGauge, PersonaCardIdentity } from '@/components/admin/personas/persona-card';
import { PersonaFilterFields } from '@/components/admin/personas/persona-filter-fields';
import { FilterBar } from '@/components/admin/versioned/filter-bar';
import { FilterPanel } from '@/components/admin/versioned/filter-panel';
import { FilterSummary } from '@/components/admin/versioned/filter-summary';
import { VersionedGrid } from '@/components/admin/versioned/versioned-grid';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { duplicatePersona, fetchInvolvementLevels, fetchPersonas, restorePersona } from '@/lib/api';
import {
  activePersonaFilters,
  filterPersonas,
  hasActivePersonaFilters,
  hasHiddenPersonaFilter,
  personaAuthors,
  personaCounts,
  personaFiltersOf,
  personaSearchOf,
  readPersonaFilters,
  resetPersonaFilters,
} from '@/lib/persona-filters';
import { PERSONA_WORDING } from '@/lib/persona-wording';
import { useVersionedDuplication } from '@/lib/versioned-duplication';
import { VERSIONED_SORT_LABEL, versionedRefusal, versionedRefusalOf } from '@/lib/versioned-labels';
import type { PersonaCard, PersonaFilters } from '@matter_extractor/shared';
import { personaSortSchema } from '@matter_extractor/shared';
import { Alert, Spinner } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * La bibliothèque de personas - the screen that replaces the couture of #32
 * (refonte modulaire §4, §10, §15; issue #42).
 *
 * The same four components and eight functions of the versioned layer the
 * criterion's list mounts, plus the three things a persona contributes: its
 * wording, its two own filter controls, and the two blocks its cards carry -
 * the avatar with its subtitle, and the mini-gauge. The layer holds the
 * filtering, the sparse URL, the chips, the counting and the grid.
 *
 * ## The scale is read for the CARDS, not only for the filter
 *
 * A card's gauge draws the current scale and fills the point whose IDENTIFIER
 * the version froze, which is why `personaCardOf` deliberately serves no
 * position: a position frozen in a version would be drawn against a scale that
 * has since gained or lost a cran. So the scale is one read for the whole
 * screen - a read per card would be an N+1 on every paint.
 *
 * A grid can render before the scale lands: the gauge then has no dots to
 * draw, and the name beside it still says which cran the persona sits on. The
 * screen is never blocked on a reference list.
 *
 * ## Everything else is the criterion's list, argued there
 *
 * The whole library in one read and filtered in the page, the filters living in
 * the URL and REPLACED rather than pushed, one cache key per shape (issue #16),
 * `validateSearch` that never throws. `criteres.index.tsx` states each of them
 * at length and none of them differs here.
 */

export const Route = createFileRoute('/admin/personas/')({
  validateSearch: readPersonaFilters,
  component: PersonasPage,
});

const INTRO =
  'La voix que l’interviewer prend : un nom côté opérateur, une direction d’IA, une présentation anonyme ou nommée, un degré d’implication et un ton. Une expérience choisit un persona et peut y ajouter une surcouche de direction, rien d’autre.';

function PersonasPage() {
  const { email } = useAdminSession();
  const filters = personaFiltersOf(Route.useSearch());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /** Open when a link arrives with one of the drawer's filters already armed. */
  const [expanded, setExpanded] = useState(() => hasHiddenPersonaFilter(filters));

  const personas = useQuery({
    queryKey: ['personas', email],
    queryFn: fetchPersonas,
    retry: false,
  });

  // The same key Réglages > Personas writes: same fetcher, same schema, same
  // shape. A second key for one list is how two screens end up disagreeing.
  const scale = useQuery({
    queryKey: ['involvement-levels', email],
    queryFn: fetchInvolvementLevels,
    retry: false,
  });

  const rejected = useRejectOn401(personas.error);

  const restore = useMutation({
    mutationFn: (card: PersonaCard) => restorePersona(card.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['personas', email] }),
  });

  /**
   * « Dupliquer », from the card's « … » menu (§4.2, issue #116).
   *
   * The mutation itself is the layer's (`lib/versioned-duplication.ts`): the
   * gesture is a route of the versioning FACTORY, and the four libraries call
   * it with the three things that differ - the API call, the list's cache key,
   * and the address of the copy's editor, which only this route can type
   * against the route tree.
   */
  const duplicate = useVersionedDuplication<PersonaCard>({
    duplicate: duplicatePersona,
    listKey: 'personas',
    onCopy: (personaId) =>
      void navigate({
        to: '/admin/personas/$personaId',
        params: { personaId },
        search: { copie: true },
      }),
  });

  /** One dimension at a time, merged into the address bar - and REPLACED there. */
  const change = (patch: Partial<PersonaFilters>) =>
    navigate({
      to: '/admin/personas',
      search: personaSearchOf({ ...filters, ...patch }),
      replace: true,
    });

  const levels = scale.data?.levels ?? [];
  const cards = personas.data?.personas ?? [];
  const counts = personaCounts(cards, filters, levels);
  const chips = activePersonaFilters(filters, levels);
  const refused = versionedRefusalOf(restore.error);
  const duplicateRefused = versionedRefusalOf(duplicate.error);

  return (
    <section className="space-y-5 pb-16">
      <header className="space-y-1">
        {/*
          `/^personas$/i` is what `admin-shell.spec.ts` and the door catalogue
          read: the couture carried this exact heading, and the real screen keeps
          it so those assertions never had a screen to chase.
        */}
        <h1 className="font-bold text-bleu-nuit text-h2">Personas</h1>
        <p className="max-w-[95ch] text-gray-600 text-small">{INTRO}</p>
      </header>

      <FilterBar
        wording={PERSONA_WORDING}
        filters={filters}
        authors={personaAuthors(cards, filters)}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((was) => !was)}
        onChange={change}
      />

      {expanded ? (
        <FilterPanel
          wording={PERSONA_WORDING}
          filters={filters}
          ownFilters={<PersonaFilterFields filters={filters} levels={levels} onChange={change} />}
          retiredCount={counts.retired}
          onChange={change}
          onReset={() =>
            navigate({
              to: '/admin/personas',
              search: personaSearchOf(resetPersonaFilters(filters)),
              replace: true,
            })
          }
          resetDisabled={!hasActivePersonaFilters(filters)}
        />
      ) : null}

      <FilterSummary
        wording={PERSONA_WORDING}
        chips={chips}
        counts={counts}
        sort={filters.sort}
        sortOptions={personaSortSchema.options}
        // The four common sorts and no more: a persona adds none (§15).
        sortLabel={VERSIONED_SORT_LABEL}
        onChange={change}
      />

      {duplicate.isError ? (
        <div data-testid="personas-duplication-refusee">
          <Alert tone="danger">
            {duplicateRefused === null
              ? 'La copie n’a pas pu être écrite. Réessayez dans un instant.'
              : versionedRefusal(duplicateRefused, PERSONA_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}

      {restore.isError ? (
        <div data-testid="personas-restauration-refusee">
          <Alert tone="danger">
            {refused === null
              ? 'La restauration a échoué. Réessayez dans un instant.'
              : versionedRefusal(refused, PERSONA_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}

      {personas.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement des personas" />
        </div>
      ) : null}

      {personas.isError && !rejected ? (
        <p className="text-gray-700">La bibliothèque de personas n’a pas pu être chargée.</p>
      ) : null}

      {personas.data ? (
        <VersionedGrid
          wording={PERSONA_WORDING}
          cards={filterPersonas(cards, filters, levels)}
          emptyMessage={
            chips.length === 0
              ? 'Aucun persona pour l’instant. La carte en pointillé ouvre l’éditeur vide.'
              : 'Aucun persona ne répond à ces filtres. Retirez une pastille, ou réinitialisez.'
          }
          // Nothing: what a criterion says in a sentence, a persona says in its
          // avatar and its gauge (§15). The usage line and the stamp under them
          // are the layer's, unchanged.
          facetsOf={() => []}
          renderIdentity={(card) => <PersonaCardIdentity card={card} />}
          renderExtra={(card) => <PersonaCardGauge card={card} levels={levels} />}
          renderCardLink={(card, props) => (
            <Link
              to="/admin/personas/$personaId"
              params={{ personaId: card.id }}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          renderCreateLink={(props) => (
            <Link
              to="/admin/personas/nouveau"
              data-testid={props['data-testid']}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          onRestore={(card) => restore.mutate(card)}
          restoringId={restore.isPending ? (restore.variables?.id ?? null) : null}
          onDuplicate={(card, tag) => duplicate.mutate({ card, tag })}
          duplicatingId={duplicate.isPending ? (duplicate.variables?.card.id ?? null) : null}
        />
      ) : null}
    </section>
  );
}
